import './output.css'

import React, { Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router'
import { Permission } from 'extra/types/Permissions'
import { AuthProvider, useAuth } from './context/AuthContext'
import { EntitiesProvider } from './context/EntitiesContext'
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { NewPromptPage } from './pages/NewPromptPage'
import { MainLayout } from './components/MainLayout'
import { RequirePermission } from './components/RequirePermission'

const PromptDetailPage = React.lazy(() => import('./pages/PromptDetailPage').then(m => ({ default: m.PromptDetailPage })))
const DeploymentPage = React.lazy(() => import('./pages/DeploymentPage').then(m => ({ default: m.DeploymentPage })))
const DatasetPage = React.lazy(() => import('./pages/DatasetPage').then(m => ({ default: m.DatasetPage })))
const SettingsPage = React.lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const ExecutionStatsPage = React.lazy(() => import('./pages/ExecutionStatsPage').then(m => ({ default: m.ExecutionStatsPage })))
const ExecutionsPage = React.lazy(() => import('./pages/ExecutionsPage').then(m => ({ default: m.ExecutionsPage })))
const PromptSettingsPage = React.lazy(() => import('./pages/PromptSettingsPage').then(m => ({ default: m.PromptSettingsPage })))
const SimulationPage = React.lazy(() => import('./pages/SimulationPage').then(m => ({ default: m.SimulationPage })))

function LazyFallback () {
  return <div className="text-gray-400 p-4">Loading...</div>
}

function ProtectedRoute ({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function AppRoutes () {
  const { isAuthenticated, login } = useAuth()

  return (
    <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <LoginPage onLogin={login} />
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <EntitiesProvider>
                <MainLayout />
              </EntitiesProvider>
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="executions/stats" element={<ExecutionStatsPage />} />
          <Route path="executions" element={<RequirePermission permission={Permission.VIEW_EXECUTIONS} fallback={<Navigate to="/" replace />}><ExecutionsPage /></RequirePermission>} />
          <Route path="prompts/new" element={<NewPromptPage />} />
          <Route path="prompts/:id" element={<PromptDetailPage />} />
          <Route path="prompts/:id/deployments" element={<DeploymentPage />} />
          <Route path="prompts/:id/datasets" element={<DatasetPage />} />
          <Route path="prompts/:id/simulations" element={<SimulationPage />} />
          <Route path="prompts/:id/settings" element={<PromptSettingsPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

export function App () {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  )
}

